/**
 * TokenStore — Kapselung von localStorage für JWT und Vertrauensgeräte.
 * Handles Serialisierung/Deserialisierung und Expiration-Prüfung.
 */

import { STORAGE_KEYS } from '../utils/constants'
import type { TrustedDevice } from '../types/auth'

/**
 * Holt das gespeicherte JWT aus localStorage.
 *
 * @returns JWT-String oder null falls nicht gesetzt
 */
export function getToken(): string | null {
  const token = localStorage.getItem(STORAGE_KEYS.jwt)
  return token
}

/**
 * Speichert ein JWT in localStorage.
 *
 * @param token JWT-String
 */
export function setToken(token: string): void {
  localStorage.setItem(STORAGE_KEYS.jwt, token)
}

/**
 * Löscht das gespeicherte JWT aus localStorage.
 */
export function clearToken(): void {
  localStorage.removeItem(STORAGE_KEYS.jwt)
}

/**
 * Holt das gespeicherte Vertrauensgerät aus localStorage.
 * Wenn das Gerät abgelaufen ist (expiresAt <= Date.now()),
 * wird es gelöscht und null zurückgegeben.
 *
 * @returns TrustedDevice-Objekt oder null falls nicht gesetzt/abgelaufen
 */
export function getTrustedDevice(): TrustedDevice | null {
  const json = localStorage.getItem(STORAGE_KEYS.trustedDevice)
  if (!json) {
    return null
  }

  try {
    const device = JSON.parse(json) as TrustedDevice
    // Prüfe Ablauf
    if (device.expiresAt <= Date.now()) {
      // Abgelaufen → löschen und null
      clearTrustedDevice()
      return null
    }
    return device
  } catch {
    return null
  }
}

/**
 * Speichert ein Vertrauensgerät in localStorage.
 *
 * @param device TrustedDevice-Objekt
 */
export function setTrustedDevice(device: TrustedDevice): void {
  localStorage.setItem(STORAGE_KEYS.trustedDevice, JSON.stringify(device))
}

/**
 * Löscht das gespeicherte Vertrauensgerät aus localStorage.
 */
export function clearTrustedDevice(): void {
  localStorage.removeItem(STORAGE_KEYS.trustedDevice)
}

/**
 * Holt das Vertrauensgerät, nur wenn es noch gültig ist und die Email passt.
 *
 * @param email E-Mail-Adresse zum Vergleich
 * @returns TrustedDevice-Objekt oder null falls nicht vorhanden/abgelaufen/Email unterschiedlich
 */
export function getValidTrustedDevice(email: string): TrustedDevice | null {
  const device = getTrustedDevice()
  if (!device) {
    return null
  }

  // Email muss exakt übereinstimmen (case-sensitive)
  if (device.email !== email) {
    return null
  }

  return device
}
